import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    AthleteAdminBreadcrumbNav,
    AthleteAdminNavState,
    getTeamMemberBreadcrumbLabel,
    navigateToTeamMemberFromBreadcrumb,
    useAdminAthleteDisplayName,
} from "./AthleteAdminBreadcrumbNav";
import ViewTaskModal from "./ViewTaskModal";
import AddTaskModal from "./AddTaskModal";
import DeleteTaskModal from "./DeleteTaskModal";
import Pagination from "./Pagination";
import { isAthlete, isGuardian } from "../utils/auth";
import { useAuth } from "../contexts/AuthContext";
import { athleteProfileFetchPath, resolveAthleteCollegeUsername } from "../utils/viewedAthlete";
import { buildApiUrl } from "../utils/apiConfig";
import "../styles/athlete-admin-layout.css";
import "../styles/athlete-admin-scaled-wrapper.css";
import "../styles/game-plan.css";
import "../styles/pagination.css";
import "../styles/athlete-admin-page-spacing.css";
import EyeIcon from "../images/buttons/Eye.svg";
import EditIcon from "../images/buttons/Edit.svg";
import DeleteIcon from "../images/buttons/Delete.svg";
import { apiClient } from "../utils/apiClient";

interface GamePlanTask {
    id: number;
    title: string;
    description: string;
    filePath: string | null;
    dueAt: string;
    allDay: boolean;
    courseId?: number | null;
    courseName?: string | null;
    courseImageUrl?: string | null;
    completed?: boolean;
}

interface GamePlanResponse {
    tasks: GamePlanTask[];
    totalElements: number;
    totalPages: number;
    page: number;
    size: number;
}

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
}

export default function GamePlanPage() {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { userInfo, isLoading: authLoading } = useAuth();
    const PAGE_SIZE = 7;
    const [tasks, setTasks] = useState<GamePlanTask[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [coachData, setCoachData] = useState<CoachData | null>(null);
    const [athleteStaffId, setAthleteStaffId] = useState<number | null>(null);
    const [athleteStaffName, setAthleteStaffName] = useState<string | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
    const [hasAnyTasks, setHasAnyTasks] = useState(false);
    const [athleteEmail, setAthleteEmail] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(0);
    const tasksBodyRef = useRef<HTMLDivElement>(null);
    const skipTabFetchRef = useRef(true);

    // Unified modal state for both view and edit
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
    const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

    // Add Task Modal state
    const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);

    // Delete Modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<{id: number, title: string} | null>(null);

    // Check if current user is admin/staff viewing an athlete profile
    const isAdminViewingAthlete = !isAthlete() && !isGuardian() && Boolean(athleteId);
    const guardianReadOnly = isGuardian();
    const canMutateTasks = !guardianReadOnly;

    // Get team member info from navigation state
    const navState = location.state as AthleteAdminNavState | null;

    const athleteBreadcrumbName = useAdminAthleteDisplayName(athleteId, navState?.athleteDisplayName);

    // Handler for breadcrumb navigation to team member view
    const handleNavigateToTeamMember = () => {
        navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId);
    };

    const applyGamePlanResponse = async (data: GamePlanResponse, email: string, completedParam: boolean) => {
        setTasks(data.tasks || []);
        setTotalElements(data.totalElements ?? 0);
        setTotalPages(data.totalPages ?? 0);

        try {
            const oppositeResponse = await apiClient(
                buildApiUrl(
                    `api/athletes/gameplan?username=${encodeURIComponent(email)}&completed=${!completedParam}&page=0&size=1`
                ),
                {
                    headers: { Accept: "application/json" },
                    credentials: "include",
                }
            );

            if (oppositeResponse.ok) {
                const oppositeData: GamePlanResponse = await oppositeResponse.json();
                const currentTabHasTasks = (data.totalElements ?? 0) > 0;
                const oppositeTabHasTasks = (oppositeData.totalElements ?? 0) > 0;
                setHasAnyTasks(currentTabHasTasks || oppositeTabHasTasks);
            } else {
                setHasAnyTasks((data.totalElements ?? 0) > 0);
            }
        } catch (oppositeError) {
            console.error("Failed to fetch opposite game plan data:", oppositeError);
            setHasAnyTasks((data.totalElements ?? 0) > 0);
        }
    };

    const fetchTasksForTab = async (email: string, tab: 'active' | 'completed', page = 0) => {
        const completedParam = tab === 'completed';
        const response = await apiClient(
            buildApiUrl(
                `api/athletes/gameplan?username=${encodeURIComponent(email)}&completed=${completedParam}&page=${page}&size=${PAGE_SIZE}`
            ),
            {
                headers: { Accept: "application/json" },
                credentials: "include",
            }
        );

        if (!response.ok) {
            console.warn("Could not fetch game plan:", response.status);
            setTasks([]);
            setTotalElements(0);
            setTotalPages(0);
            setHasAnyTasks(false);
            return;
        }

        const data: GamePlanResponse = await response.json();
        await applyGamePlanResponse(data, email, completedParam);
    };

    useEffect(() => {
        skipTabFetchRef.current = true;
        setTasks([]);
        setHasAnyTasks(false);
        setAthleteEmail('');
        setCurrentPage(0);
        setActiveTab('active');
    }, [athleteId]);

    useEffect(() => {
        const loadAthleteAndTasks = async () => {
            if (authLoading && !athleteId) {
                setInitialLoading(true);
                return;
            }

            try {
                setInitialLoading(true);
                setError(null);

                if (navState?.teamMemberName) {
                    const nameParts = navState.teamMemberName.split(' ');
                    setCoachData({
                        id: navState.teamMemberId,
                        firstName: nameParts[0] || navState.teamMemberName,
                        lastName: nameParts.slice(1).join(' ') || '',
                    });
                }

                let resolvedAthleteEmail = '';
                let profilePath = athleteProfileFetchPath(athleteId);
                if (!profilePath && isAthlete()) {
                    profilePath = "/api/athletes/me";
                }

                if (profilePath) {
                    const athleteResponse = await apiClient(buildApiUrl(profilePath), {
                        headers: { Accept: "application/json" },
                        credentials: "include",
                    });

                    if (athleteResponse.ok) {
                        const athleteData = await athleteResponse.json();
                        resolvedAthleteEmail = isAdminViewingAthlete
                            ? resolveAthleteCollegeUsername(athleteData.email, athleteData.username)
                            : resolveAthleteCollegeUsername(
                                  athleteData.email,
                                  athleteData.username,
                                  userInfo?.userId,
                              );
                        if (typeof athleteData.staffId === 'number' && athleteData.staffId > 0) {
                            setAthleteStaffId(athleteData.staffId);
                        } else {
                            setAthleteStaffId(null);
                        }
                        if (typeof athleteData.staffName === 'string' && athleteData.staffName.trim()) {
                            setAthleteStaffName(athleteData.staffName.trim());
                        } else {
                            setAthleteStaffName(null);
                        }
                    } else if (!athleteId && !isGuardian()) {
                        resolvedAthleteEmail = resolveAthleteCollegeUsername(userInfo?.userId);
                    }
                } else {
                    resolvedAthleteEmail = resolveAthleteCollegeUsername(userInfo?.userId);
                }

                if (!resolvedAthleteEmail) {
                    setError("We couldn't load your game plan. Please refresh the page.");
                    return;
                }

                setAthleteEmail(resolvedAthleteEmail);
                await fetchTasksForTab(resolvedAthleteEmail, activeTab, 0);
                setCurrentPage(0);
            } catch (loadError) {
                setError("We couldn't load your game plan. Please refresh the page.");
            } finally {
                setInitialLoading(false);
                skipTabFetchRef.current = false;
            }
        };

        loadAthleteAndTasks();
    }, [athleteId, authLoading, userInfo?.userId]); // eslint-disable-line react-hooks/exhaustive-deps -- tab changes handled separately

    useEffect(() => {
        if (skipTabFetchRef.current || !athleteEmail) {
            return;
        }

        const loadTabTasks = async () => {
            try {
                setTabLoading(true);
                await fetchTasksForTab(athleteEmail, activeTab, 0);
                setCurrentPage(0);
            } catch (tabError) {
                console.error("Failed to fetch tab tasks:", tabError);
            } finally {
                setTabLoading(false);
            }
        };

        loadTabTasks();
    }, [activeTab, athleteEmail]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddNewTask = () => {
        setAddTaskModalOpen(true);
    };

    const handleTaskCreated = async () => {
        // Refresh the task list after creating a new task
        await refreshTaskList();
    };

    const refreshTaskList = async (page = 0) => {
        if (!athleteEmail) {
            return;
        }

        try {
            await fetchTasksForTab(athleteEmail, activeTab, page);
            setCurrentPage(page);
        } catch (refreshError) {
            console.error("Failed to refresh tasks:", refreshError);
        }
    };

    const handleTabChange = (tab: 'active' | 'completed') => {
        if (tab === activeTab || tabLoading) {
            return;
        }
        setActiveTab(tab);
    };

    const handleEditTask = (taskId: number) => {
        setSelectedTaskId(taskId);
        setModalMode('edit');
        setModalOpen(true);
    };

    const handleViewTask = (taskId: number) => {
        setSelectedTaskId(taskId);
        setModalMode('view');
        setModalOpen(true);
    };

    const handleDeleteTask = (taskId: number) => {
        // Find the task to get its title
        const task = tasks.find(t => t.id === taskId);
        if (task) {
            setTaskToDelete({ id: taskId, title: task.title });
            setDeleteModalOpen(true);
        }
    };

    const confirmDeleteTask = async () => {
        if (!taskToDelete) return;

        try {

            // Use the athleteEmail from state
            if (!athleteEmail) {
                alert("Unable to delete task: No athlete email found");
                setDeleteModalOpen(false);
                setTaskToDelete(null);
                return;
            }

            const response = await apiClient(buildApiUrl(`api/tasks/${taskToDelete.id}/athlete/${encodeURIComponent(athleteEmail)}`), {
                method: 'DELETE',
                credentials: 'include' // Use HTTP-only cookie for auth
            });

            if (response.ok) {
                // If this was the last task on the current page, go back one page
                const newPage = tasks.length === 1 && currentPage > 0 ? currentPage - 1 : currentPage;
                await refreshTaskList(newPage);

                // Close the delete modal
                setDeleteModalOpen(false);
                setTaskToDelete(null);
            } else {
                const errorText = await response.text();
                alert(`Failed to delete task: ${errorText}`);
                setDeleteModalOpen(false);
                setTaskToDelete(null);
            }
        } catch (error: any) {
            console.error('Failed to delete task:', error);
            alert('Failed to delete task. Please try again.');
            setDeleteModalOpen(false);
            setTaskToDelete(null);
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedTaskId(null);
        setModalMode('view');
    };

    const closeAddTaskModal = () => {
        setAddTaskModalOpen(false);
    };

    const handlePageChange = (page: number) => {
        refreshTaskList(page);
        tasksBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const adminBreadcrumb = isAdminViewingAthlete ? (
        <AthleteAdminBreadcrumbNav
            pageLabel="Game Plan"
            teamMemberLabel={getTeamMemberBreadcrumbLabel(
                coachData,
                navState?.teamMemberName,
                athleteStaffName ?? undefined
            )}
            onTeamMemberClick={handleNavigateToTeamMember}
            athleteLabel={athleteBreadcrumbName}
            athleteProfileId={athleteId}
        />
    ) : null;

    if (initialLoading) {
        return (
            <div className="game-plan-wrapper">
                {adminBreadcrumb}
                <div className="game-plan-container">
                    <div className="loading-spinner">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="game-plan-wrapper">
                {adminBreadcrumb}
                <div className="game-plan-container">
                    <div className="error-message">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`game-plan-wrapper${hasAnyTasks ? ' game-plan-wrapper--listed' : ''}`}>
            {adminBreadcrumb}

            <div className="game-plan-container">

                {hasAnyTasks ? (
                    <div className="game-plan-content">
                        <div className="game-plan-tasks-panel game-plan-tasks-panel--listed">
                            <div className={`game-plan-tabs-row${isAdminViewingAthlete ? ' game-plan-tabs-row--admin' : ''}`}>
                                <div className="game-plan-tabs">
                                    <button
                                        type="button"
                                        className={`tab ${activeTab === 'active' ? 'active' : ''}`}
                                        onClick={() => handleTabChange('active')}
                                        disabled={tabLoading}
                                        aria-selected={activeTab === 'active'}
                                    >
                                        Active
                                    </button>
                                    <button
                                        type="button"
                                        className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
                                        onClick={() => handleTabChange('completed')}
                                        disabled={tabLoading}
                                        aria-selected={activeTab === 'completed'}
                                    >
                                        Completed
                                    </button>
                                </div>
                                {isAdminViewingAthlete && (
                                    <button className="add-new-task-btn" onClick={handleAddNewTask}>
                                        <span className="add-icon">+</span>
                                        Add new task
                                    </button>
                                )}
                            </div>

                            <div className="tasks-section">
                                {!isAdminViewingAthlete && (
                                    <div className="tasks-header">
                                        <h2 className="tasks-title">Your Tasks</h2>
                                        {canMutateTasks && (
                                        <button className="add-new-task-btn" onClick={handleAddNewTask} data-testid="game-plan-add-task">
                                            <span className="add-icon">+</span>
                                            Add new task
                                        </button>
                                        )}
                                    </div>
                                )}

                                <div
                                    ref={tasksBodyRef}
                                    className={`game-plan-tasks-body${tabLoading ? " is-loading" : ""}${tasks.length > 0 ? " game-plan-tasks-body--listed" : ""}`}
                                    aria-busy={tabLoading}
                                >
                                    {tabLoading ? (
                                        <div className="game-plan-tab-loading" aria-live="polite">
                                            Loading...
                                        </div>
                                    ) : tasks.length === 0 ? (
                                        <div className="empty-tasks-message">
                                            {activeTab === 'active'
                                                ? 'There are no active tasks'
                                                : 'There are no completed tasks'}
                                        </div>
                                    ) : (
                                        <div className="tasks-list">
                                            {tasks.map((task) => (
                                                <div key={task.id} className="task-item">
                                                    <div className="task-content">
                                                        <div className="task-title-group">
                                                            <h3 className="task-title">{task.title}</h3>
                                                            {task.courseImageUrl && (
                                                                <div className="task-course-badge" title={task.courseName || 'Course'}>
                                                                    <img src={task.courseImageUrl} alt={task.courseName || 'Course'} className="task-course-icon" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        {task.description && (
                                                            <p className="task-description-preview">{task.description}</p>
                                                        )}
                                                    </div>
                                                    <div className="task-item-aside">
                                                        {task.dueAt && (
                                                            <div className="task-due-date">
                                                                <span className="due-date-label">Complete by:</span>
                                                                <span className="due-date-value">
                                                                    {task.allDay
                                                                        ? new Date(task.dueAt).toLocaleDateString()
                                                                        : new Date(task.dueAt).toLocaleString()}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="task-actions">
                                                            <button className="task-action-btn view-btn" title="View task" onClick={() => handleViewTask(task.id)}>
                                                                <img src={EyeIcon} alt="View" />
                                                            </button>
                                                            {canMutateTasks && (
                                                            <>
                                                            <button className="task-action-btn edit-btn" title="Edit task" onClick={() => handleEditTask(task.id)} data-testid="game-plan-edit-task">
                                                                <img src={EditIcon} alt="Edit" />
                                                            </button>
                                                            <button className="task-action-btn delete-btn" title="Delete task" onClick={() => handleDeleteTask(task.id)} data-testid="game-plan-delete-task">
                                                                <img src={DeleteIcon} alt="Delete" />
                                                            </button>
                                                            </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                        // Show the full empty state when there are NO tasks at all across both tabs
                        <div className="empty-game-plan">
                            <div className="empty-game-plan-hero">
                                <div className="empty-game-plan-icon">
                                    <div className="folder-icon">
                                        <div className="folder-body"></div>
                                        <div className="folder-tab"></div>
                                        <div className="plus-icon">
                                            <span className="plus-symbol">+</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="empty-game-plan-copy">
                                    <h2 className="empty-game-plan-title">Get started creating your game plan</h2>
                                    <p className="empty-game-plan-description">
                                        These helpful tasks will keep you organized and on track as you work toward your academic and athletic goals.
                                    </p>
                                    {canMutateTasks && (
                                    <button className="add-new-task-btn" onClick={handleAddNewTask} data-testid="game-plan-add-task-empty">
                                        <span className="add-icon">+</span>
                                        Add new task
                                    </button>
                                    )}
                                </div>
                            </div>
                        </div>
                )}

                {/* Unified Task Modal (for both viewing and editing) */}
                {modalOpen && selectedTaskId !== null && (
                    <ViewTaskModal
                        open={modalOpen}
                        taskId={selectedTaskId}
                        mode={modalMode} // Pass the mode (view/edit) to the modal
                        onClose={closeModal}
                        onTaskUpdated={refreshTaskList}
                        athleteEmail={athleteEmail}
                        isAthleteEditing={!athleteId && isAthlete()}
                    />
                )}

                {/* Add Task Modal */}
                {addTaskModalOpen && (
                    <AddTaskModal
                        open={addTaskModalOpen}
                        onClose={closeAddTaskModal}
                        onTaskCreated={handleTaskCreated}
                        preselectedAthleteEmail={athleteEmail}
                        isAthleteCreating={!athleteId && isAthlete()}
                    />
                )}

                {/* Delete Task Confirmation Modal */}
                <DeleteTaskModal
                    open={deleteModalOpen}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setTaskToDelete(null);
                    }}
                    onConfirm={confirmDeleteTask}
                    taskTitle={taskToDelete?.title}
                />
            </div>

            {hasAnyTasks && !tabLoading && totalElements > 0 && (
                <div className="game-plan-pagination-footer" data-testid="game-plan-pagination-footer">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={Math.max(totalPages, 1)}
                        totalElements={totalElements}
                        onPageChange={handlePageChange}
                        pageSize={PAGE_SIZE}
                    />
                </div>
            )}
        </div>
    );
}
